import { useEffect, useState } from "react";
import { CalendarLinks } from "../../components/CalendarLinks";
import { TwoStepSetup } from "../../components/TwoStepSetup";
import { doc, onSnapshot } from "firebase/firestore";
import { getDownloadURL, ref as storageRef, uploadBytes } from "firebase/storage";
import { BadgeCheck, Camera } from "lucide-react";
import { MemberProfileSchema, paths, type MemberProfile } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label } from "@clevis/shared-ui";
import { db, storage } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { compressImage } from "../../lib/imageCompress";
import { useAuth } from "../../hooks/useAuth";
import { textareaClass } from "../../components/JobEditCard";
import { CallerPopPreference } from "../../components/IncomingCallBanner";

// "My profile": what a customer sees when the "who's coming" text arrives —
// your photo, the name you go by, a couple of lines about you. An admin
// approves it before it goes public (and again after any change).

export function ProfilePage() {
  const { tenantId, user } = useAuth();
  const [saved, setSaved] = useState<MemberProfile | null>(null);
  const [form, setForm] = useState({ publicName: "", bio: "", years: "", certs: "" });
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!tenantId || !user) return;
    return onSnapshot(doc(db, paths.tenantMember(tenantId, user.uid)), (snap) => {
      const p = MemberProfileSchema.parse((snap.get("profile") as Partial<MemberProfile> | undefined) ?? {});
      setSaved(p);
      setForm({
        publicName: p.publicName || ((snap.get("displayName") as string | undefined) ?? "").split(/\s+/)[0] || "",
        bio: p.bio,
        years: p.yearsExperience !== null ? String(p.yearsExperience) : "",
        certs: p.certifications.join(", "),
      });
      setPhotoUrl(p.photoUrl);
    });
  }, [tenantId, user]);

  async function uploadPhoto(file: File | undefined) {
    if (!file || !tenantId || !user) return;
    setBusy("photo");
    setNote("");
    try {
      const blob = await compressImage(file);
      const r = storageRef(storage, paths.memberPhotoStorage(tenantId, user.uid));
      await uploadBytes(r, blob, { contentType: "image/jpeg" });
      const url = await getDownloadURL(r);
      await call("setMemberProfile", { tenantId, photoUrl: url });
      setNote("Photo saved. An admin approves it before customers see it.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function save() {
    if (!tenantId) return;
    setBusy("save");
    setNote("");
    try {
      const years = form.years.trim() ? Math.max(0, Math.min(70, parseInt(form.years, 10) || 0)) : null;
      const res = await call<{ approved: boolean }>("setMemberProfile", {
        tenantId,
        publicName: form.publicName.trim(),
        bio: form.bio.trim(),
        yearsExperience: years,
        certifications: form.certs.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 10),
      });
      setNote(res.approved ? "Saved." : "Saved. An admin approves it before customers see it.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="mx-auto max-w-xl space-y-4">
      <h1 className="text-lg font-bold">My profile</h1>
      <TwoStepSetup />
      <p className="text-xs text-steel">
        When you're on the way, the customer gets a text with a link to this: your photo, the name you go by, and a few words
        about you. People trust a face. {saved?.approved ? "It's approved and live." : "An admin approves it before customers see it."}
      </p>

      <Card className="space-y-3">
        <div className="flex items-center gap-3">
          {photoUrl ? (
            <img src={photoUrl} alt="" className="h-20 w-20 rounded-full border border-steel/20 object-cover" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-steel/15 text-2xl font-bold text-ink">{form.publicName.slice(0, 1) || "?"}</div>
          )}
          <label className="cursor-pointer">
            <input type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => void uploadPhoto(e.target.files?.[0])} />
            <span className="inline-flex items-center gap-1 rounded-lg border border-steel/30 px-3 py-1.5 text-sm text-ink hover:border-ink">
              <Camera size={14} /> {busy === "photo" ? "Uploading…" : photoUrl ? "Change photo" : "Add a photo"}
            </span>
          </label>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label>Name customers see</Label>
            <Input value={form.publicName} maxLength={40} onChange={(e) => setForm({ ...form, publicName: e.target.value })} />
          </div>
          <div>
            <Label>Years of experience</Label>
            <Input type="number" min="0" max="70" value={form.years} onChange={(e) => setForm({ ...form, years: e.target.value })} />
          </div>
        </div>
        <div>
          <Label>About you ({280 - form.bio.length} left)</Label>
          <textarea
            className={textareaClass}
            rows={3}
            maxLength={280}
            placeholder="e.g. 12 years on garage doors. Dad of three, Raiders fan. I'll leave your garage cleaner than I found it."
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
          />
        </div>
        <div>
          <Label>Certifications (comma separated)</Label>
          <Input value={form.certs} placeholder="e.g. IDA certified, LiftMaster trained" onChange={(e) => setForm({ ...form, certs: e.target.value })} />
        </div>
        <div className="flex items-center gap-2">
          <Button busy={busy === "save"} onClick={() => void save()}>
            Save profile
          </Button>
          {saved?.backgroundChecked ? (
            <span className="flex items-center gap-1 text-xs text-success">
              <BadgeCheck size={13} /> Background checked
            </span>
          ) : null}
          {note ? <span className="text-xs text-steel">{note}</span> : null}
        </div>
      </Card>

      <Card>
        <CardTitle>What the customer sees</CardTitle>
        <div className="flex gap-3">
          {photoUrl ? <img src={photoUrl} alt="" className="h-16 w-16 rounded-full object-cover" /> : <div className="h-16 w-16 rounded-full bg-steel/15" />}
          <div className="min-w-0">
            <div className="font-bold text-ink">{form.publicName || "Your name"}</div>
            {form.years ? <div className="text-xs text-steel">{form.years} years of experience</div> : null}
            <p className="text-sm text-ink">{form.bio || "A couple of lines about you."}</p>
          </div>
        </div>
      </Card>

      <CallerPopPreference />

      {tenantId ? <CalendarLinks tenantId={tenantId} /> : null}
    </div>
  );
}
